import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Grid3x3, List } from 'lucide-react';
import { summaryApi } from '../../common';

function ProductsInCategories() {
    const { category } = useParams();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [sortBy, setSortBy] = useState('default');

    // Category display names and emojis
    const categoryInfo = {
        'smartphones': { name: 'Smartphones', emoji: '📱', description: 'Cutting-edge smartphones from Apple, Samsung, Google and more' },
        'laptops': { name: 'Laptops', emoji: '💻', description: 'High-performance laptops for work, creativity, and gaming' },
        'tablets': { name: 'Tablets', emoji: '📟', description: 'Perfect blend of productivity and entertainment' },
        'smart-watches': { name: 'Smart Watches', emoji: '⌚', description: 'Stay connected and track your fitness with style' },
        'drones': { name: 'Drones', emoji: '🚁', description: 'Capture stunning aerial photography and videography' },
        'accessories': { name: 'Accessories', emoji: '🔌', description: 'Essential accessories to enhance your devices' },
        'games-consoles': { name: 'Gaming Consoles', emoji: '🎮', description: 'Next-gen gaming experience for enthusiasts' },
    };

    const currentCategory = categoryInfo[category] || { name: category, emoji: '📦', description: 'Browse our collection' };

    useEffect(() => {
        fetchCategoryProducts();
    }, [category]);

    const fetchCategoryProducts = async () => {
        setLoading(true);
        try {
            const response = await fetch(summaryApi.allProductCategories.url, {
                method: summaryApi.allProductCategories.method,
                credentials: "include",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    productsCategory: category
                })
            });
            const responseData = await response.json();
            setProducts(responseData?.data || []);
        } catch (error) {
            console.error('Error fetching products:', error);
            setProducts([]);
        }
        setLoading(false);
    };

    const handleSortChange = (value) => {
        setSortBy(value);
        let sortedProducts = [...products];
        
        switch(value) {
            case 'price-low':
                sortedProducts.sort((a, b) => a.productPrice - b.productPrice);
                break;
            case 'price-high':
                sortedProducts.sort((a, b) => b.productPrice - a.productPrice);
                break;
            case 'name':
                sortedProducts.sort((a, b) => a.productName.localeCompare(b.productName));
                break;
            default:
                break;
        }
        
        setProducts(sortedProducts);
    };

    const goBack = () => {
        navigate(-1);
    };

    const handleProductClick = (productId) => {
        navigate(`/product/${productId}`);
    };

    // Product Card Component
    const ProductCard = ({ product, isListView = false }) => {
        const discountedPrice = product.productPrice - (product.productPrice * product.productDiscount / 100);
        
        return (
            <div
                onClick={() => handleProductClick(product._id)}
                className={`group cursor-pointer bg-white border border-neutral-200 rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-neutral-300 ${
                    isListView ? 'flex flex-col sm:flex-row' : ''
                }`}
            >
                {/* Image Container */}
                <div className={`relative overflow-hidden bg-neutral-50 ${
                    isListView ? 'w-full sm:w-48 h-48 sm:h-auto flex-shrink-0' : 'w-full h-56 sm:h-64'
                }`}>
                    <img
                        src={product.productImage[0]}
                        alt={product.productName}
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                    />
                    {product.productDiscount > 0 && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                            -{product.productDiscount}%
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className={`p-4 ${isListView ? 'flex-1 flex flex-col justify-between' : ''}`}>
                    <div>
                        <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1" style={{ fontFamily: 'Georgia, serif' }}>
                            {product.productBrand}
                        </p>
                        <h3 
                            className="text-sm sm:text-base font-normal text-neutral-900 mb-2 line-clamp-2 group-hover:text-neutral-600 transition-colors"
                            style={{ fontFamily: 'Georgia, serif' }}
                        >
                            {product.productName}
                        </h3>
                    </div>

                    <div className="mt-3">
                        <div className="flex items-baseline gap-2">
                            <span 
                                className="text-lg sm:text-xl font-normal text-neutral-900"
                                style={{ fontFamily: 'Georgia, serif' }}
                            >
                                ₦{discountedPrice.toLocaleString()}
                            </span>
                            {product.productDiscount > 0 && (
                                <span 
                                    className="text-sm text-neutral-500 line-through"
                                    style={{ fontFamily: 'Georgia, serif' }}
                                >
                                    ₦{product.productPrice.toLocaleString()}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Loading Skeleton
    const ProductSkeleton = ({ isListView = false }) => (
        <div className={`bg-white border border-neutral-200 rounded-lg overflow-hidden ${
            isListView ? 'flex flex-col sm:flex-row' : ''
        }`}>
            <div className={`bg-neutral-200 animate-pulse ${
                isListView ? 'w-full sm:w-48 h-48 sm:h-auto' : 'w-full h-56 sm:h-64'
            }`}></div>
            <div className={`p-4 ${isListView ? 'flex-1' : ''}`}>
                <div className="h-3 bg-neutral-200 rounded w-1/4 mb-2 animate-pulse"></div>
                <div className="h-4 bg-neutral-200 rounded w-3/4 mb-2 animate-pulse"></div>
                <div className="h-4 bg-neutral-200 rounded w-1/2 mb-4 animate-pulse"></div>
                <div className="h-6 bg-neutral-200 rounded w-1/3 animate-pulse"></div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen mt-[-40px] bg-neutral-50" style={{ fontFamily: 'Georgia, serif' }}>
            {/* Header Section */}
            <section className="bg-white border-b border-neutral-200 pt-20 sm:pt-24 pb-8 sm:pb-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Back Button */}
                    <button 
                        onClick={goBack}
                        className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors mb-6"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back to Home</span>
                    </button>

                    {/* Category Header */}
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="text-3xl sm:text-4xl">{currentCategory.emoji}</span>
                            <h1 
                                className="text-2xl sm:text-3xl lg:text-4xl font-normal text-neutral-900 tracking-tight"
                                style={{ fontWeight: 400 }}
                            >
                                {currentCategory.name}
                            </h1>
                        </div>
                        <p className="text-base sm:text-lg text-neutral-600 mb-4">
                            {currentCategory.description}
                        </p>
                        <div className="h-px w-16 bg-neutral-300"></div>
                    </div>
                </div>
            </section>

            {/* Controls Bar */}
            <div className="bg-white border-b border-neutral-200 sticky top-16 sm:top-20 z-10">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        {/* Results Count */}
                        <p className="text-sm text-neutral-600">
                            {loading ? 'Loading...' : `${products.length} ${products.length === 1 ? 'product' : 'products'} found`}
                        </p>

                        {/* Controls */}
                        <div className="flex items-center gap-3 flex-wrap">
                            {/* View Toggle */}
                            <div className="flex items-center gap-1 border border-neutral-300 rounded-lg p-1">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-2 rounded transition-colors ${
                                        viewMode === 'grid' 
                                            ? 'bg-neutral-900 text-white' 
                                            : 'text-neutral-600 hover:bg-neutral-100'
                                    }`}
                                >
                                    <Grid3x3 className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-2 rounded transition-colors ${
                                        viewMode === 'list' 
                                            ? 'bg-neutral-900 text-white' 
                                            : 'text-neutral-600 hover:bg-neutral-100'
                                    }`}
                                >
                                    <List className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Sort Select */}
                            <select
                                value={sortBy}
                                onChange={(e) => handleSortChange(e.target.value)}
                                className="px-4 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700 bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900 cursor-pointer"
                            >
                                <option value="default">Default</option>
                                <option value="price-low">Price: Low to High</option>
                                <option value="price-high">Price: High to Low</option>
                                <option value="name">Name: A to Z</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Products Grid/List */}
            <section className="py-8 sm:py-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className={viewMode === 'grid' 
                            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6'
                            : 'flex flex-col gap-4'
                        }>
                            {[...Array(8)].map((_, index) => (
                                <ProductSkeleton key={index} isListView={viewMode === 'list'} />
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-16">
                            <Package className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
                            <h3 className="text-xl text-neutral-900 mb-2">No products found</h3>
                            <p className="text-neutral-600 mb-6">
                                There are no products in this category yet.
                            </p>
                            <button
                                onClick={goBack}
                                className="inline-flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-wider text-white font-medium bg-neutral-900 rounded-lg hover:bg-neutral-800 transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Go Back
                            </button>
                        </div>
                    ) : (
                        <div className={viewMode === 'grid' 
                            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6'
                            : 'flex flex-col gap-4'
                        }>
                            {products.map((product) => (
                                <ProductCard 
                                    key={product._id} 
                                    product={product} 
                                    isListView={viewMode === 'list'}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

export default ProductsInCategories;